import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { documentsAPI } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useClientConfig } from '../api/clientConfig';
import { visibleAdminTabs } from './adminTabs';
import AppFooter from './AppFooter';

function Sidebar({ onHide }) {
  const location = useLocation();
  // Identity comes from context now, not a prop -- App.js used to hardcode
  // useState({ name: 'Admin' }), so every user saw themselves as "Admin".
  const { user, can, logout, isCommitteeMember } = useAuth();
  // manual_url is "" until the operations manual is actually published in this
  // environment (config.py::settings.manual_url) -- see manual/README.md.
  const { manual_url: manualUrl } = useClientConfig();
  // Same rule the tab strip and the /admin index redirect use (adminTabs.js)
  // -- the link and its destination must never disagree.
  const hasAdminAccess = visibleAdminTabs(can).length > 0;

  const isActive = (path) => location.pathname === path;

  // The My Tasks badge -- how many of the four things a visit there could
  // resolve are currently outstanding (GET /api/documents/my-tasks/count).
  // Deliberately NOT threaded through /me: that endpoint's own numbers only
  // refresh on login/impersonate/delegate, and a badge answering "is there
  // anything to do right now" needs to update within a session as the reader
  // actually clears things.
  //
  // Fetched on MOUNT (Sidebar persists across every route, so this is once
  // per session, not once per page) and again whenever the reader NAVIGATES
  // AWAY FROM /my-tasks -- the one place these four numbers actually change.
  // Accepted staleness: resolving something from elsewhere (e.g. dismissing a
  // mention from a document page) will not update the badge until the next
  // /my-tasks visit or reload. That is the same staleness tolerance
  // AuthContext already accepts for `permissions`/`home_scope` between
  // refresh() calls, not a new kind of imprecision.
  const [taskCount, setTaskCount] = useState(0);
  const previousPath = useRef(location.pathname);

  const refetchTaskCount = () => {
    documentsAPI.myTaskCounts()
      .then((res) => setTaskCount(res.data.total || 0))
      // Non-fatal: a badge that fails to load is simply absent, never a
      // banner -- the same rule every other best-effort widget here follows
      // (AcknowledgementBar's own precedent).
      .catch(() => {});
  };

  // Once per session, on mount -- Sidebar persists across every route, so
  // this is not a per-page-load fetch.
  useEffect(refetchTaskCount, []); // eslint-disable-line react-hooks/exhaustive-deps

  // And again whenever the reader NAVIGATES AWAY FROM /my-tasks -- the one
  // place these four numbers actually change during a session.
  useEffect(() => {
    const leftMyTasks = previousPath.current === '/my-tasks' && location.pathname !== '/my-tasks';
    previousPath.current = location.pathname;
    if (leftMyTasks) refetchTaskCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <div className="sidebar" id="app-sidebar">
      {/* Issue #176. The title and the hide control share a row so the button
          costs no vertical space -- this nav is already ~9 items tall, which is
          the complaint the issue opens with. */}
      <div className="sidebar-heading">
        <h1>📋 Docu-Mints</h1>
        {onHide && (
          <button
            type="button"
            className="nav-hide"
            onClick={onHide}
            aria-label="Hide navigation"
            aria-expanded="true"
            aria-controls="app-sidebar"
            title="Hide navigation"
          >
            ☰
          </button>
        )}
      </div>

      <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid var(--on-dark-divider)' }}>
        <p style={{ fontSize: '14px', margin: '5px 0' }}>👤 {user?.full_name || 'Unknown user'}</p>
        <p style={{ fontSize: '12px', color: 'var(--on-dark-muted)', margin: '5px 0' }}>{user?.title || ''}</p>
      </div>

      <ul className="nav-menu">
        {/* FIRST, and ABOVE the Dashboard link, as issue #173 asks. This is the
            landing screen ("/") and it is deliberately UNGATED: it searches
            only the ACTIVE policies the caller may already read, so there is
            no viewer for whom it would 403 -- the same reasoning that leaves
            Documents and My Tasks ungated below.

            Its presence is also what let the Dashboard link be gated without
            breaking the landing route: "/" no longer renders the Dashboard, so
            hiding that link can no longer strand anybody. */}
        <li className="nav-item">
          <Link
            to="/"
            className={`nav-link ${isActive('/') ? 'active' : ''}`}
          >
            🔍 Search
          </Link>
        </li>
        {/* GATED since issue #138. `dashboard` is non-hierarchical, so can()
            is EXACT for it -- false here means "will certainly 403", not a
            guess, which is what makes hiding the link trustworthy (same
            reasoning as Insights below). This is also why it subsumes the
            issue's literal ask: an account with no role assignment holds no
            grants at all, so dashboard.read is false for it anyway.

            Points at /dashboard since issue #173 moved the Start Page onto "/".
            The route is now gated too, which it never was while it was the
            landing screen. */}
        {can('dashboard', 'read') && (
          <li className="nav-item">
            <Link
              to="/dashboard"
              className={`nav-link ${isActive('/dashboard') ? 'active' : ''}`}
            >
              📊 Dashboard
            </Link>
          </li>
        )}
        {/* GATED since issue #173, having been ungated on purpose until then.
            What changed is that browsing is no longer the only way to find a
            policy -- the Start Page above is -- so an organization can take
            the browse list away from a read-only consumer, which is what the
            issue asks be made possible.

            `document_browse` is HIERARCHICAL, so can() here means "somewhere",
            which is the right question for a nav link: being wrong costs a
            hidden-but-harmless entry, never a false sense of access. Made
            non-hierarchical it would be satisfiable only org-wide and this
            link would vanish for every department-scoped user -- issue #22's
            trap.

            NOTE WHAT THIS DOES NOT REMOVE: reading a policy. /documents/:id
            stays ungated, so a search result still opens normally for somebody
            who no longer has the list. */}
        {can('document_browse', 'read') && (
          <li className="nav-item">
            <Link
              to="/documents"
              className={`nav-link ${isActive('/documents') ? 'active' : ''}`}
            >
              📄 Documents
            </Link>
          </li>
        )}
        <li className="nav-item">
          {/* NOT gated on a permission -- deliberately. Owning a document
              requires none (owner is any authenticated user, picked from
              GET /api/users/selectable), so gating this link would hide it
              from exactly the people it exists for. Same reasoning as
              Approvals just below. */}
          <Link
            to="/my-tasks"
            className={`nav-link ${isActive('/my-tasks') ? 'active' : ''}`}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <span>📋 My Tasks</span>
            {/* Only rendered when there is something to do -- a badge showing
                "0" is worse than no badge, since it trains the eye to ignore
                it. taskCount is the SAME `total` GET /my-tasks/count returns,
                which sums in_progress + coming_due + mentions +
                acknowledgements and deliberately never the "other" bucket --
                see that endpoint's own docstring. Pill styling matches the
                active-nav treatment (--teal-300/--teal-900) rather than a
                --danger/--warning tone: this is routine, expected work, not
                an alarm. */}
            {taskCount > 0 && (
              <span style={{
                background: 'var(--teal-300)', color: 'var(--teal-900)',
                borderRadius: 'var(--radius-pill)', fontSize: '11px', fontWeight: 700,
                minWidth: '20px', height: '20px', padding: '0 6px',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {taskCount}
              </span>
            )}
          </Link>
        </li>
        {/* STILL NOT gated on can('approvals', ...) -- deliberately.
            Authority over a gate is ApprovalGroupMember (or a live delegation
            covering one), not an `approvals` grant, so a committee member
            with no `approvals` permission still has a real, actionable queue;
            gating on the permission would hide the link from exactly the
            people who need it.

            Gated on isCommitteeMember ALONE (issue #150, narrowing #138):
            this used to also draw the link for anyone holding ANY role
            assignment (`hasRoles`), which meant the seeded Read Only role --
            an ordinary role like any other -- put the link in front of every
            Read Only user pointed at a queue that is always empty. Removed
            rather than kept dark; isCommitteeMember comes from /api/auth/me
            and authorizes nothing -- /api/approvals/pending is already scoped
            to the caller's own active memberships, so asking it IS the
            authorization answer. */}
        {isCommitteeMember && (
          <li className="nav-item">
            <Link
              to="/approvals"
              className={`nav-link ${isActive('/approvals') ? 'active' : ''}`}
            >
              ✅ Approvals
            </Link>
          </li>
        )}
        {/* GATED, unlike Documents / My Tasks / Approvals above. `insights` is
            non-hierarchical, so can() is EXACT for it (issue #47): false here
            means "will certainly 403", not a guess, which is what makes hiding
            the link trustworthy rather than a nuisance. */}
        {can('insights', 'read') && (
          <li className="nav-item">
            <Link
              to="/insights"
              className={`nav-link ${isActive('/insights') ? 'active' : ''}`}
            >
              🔎 Insights
            </Link>
          </li>
        )}
        {hasAdminAccess && (
          <li className="nav-item">
            <Link
              to="/admin"
              className={`nav-link ${location.pathname.startsWith('/admin') ? 'active' : ''}`}
            >
              ⚙️ Admin
            </Link>
          </li>
        )}
        <li className="nav-item">
          <Link
            to="/profile"
            className={`nav-link ${isActive('/profile') ? 'active' : ''}`}
          >
            👤 My Profile
          </Link>
        </li>
        {manualUrl && (
          <li className="nav-item">
            {/* A plain <a>, not <Link>: the manual is a separately published
                static site (see manual/README.md), not an in-app route. Opens
                in a new tab so leaving to read documentation never loses
                whatever the user was doing in the app. */}
            <a
              href={manualUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="nav-link"
              title="Opens the operations manual in a new tab"
            >
              ❓ Help
            </a>
          </li>
        )}
      </ul>

      <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid var(--on-dark-divider)' }}>
        <button className="btn btn-secondary" style={{ width: '100%' }} onClick={() => logout('user')}>
          🔓 Logout
        </button>
        <AppFooter />
      </div>
    </div>
  );
}

export default Sidebar;
